<template>
  <!-- Page Header -->
  <PageHeader
    title="Gestion des Gammes de Tailles"
    subtitle=""
    class="mb-0 pr-10"
  />

  <div class="w-full px-2 sm:px-6 py-4 space-y-8">
    <!-- Bouton de retour conditionnel -->
    <div v-if="returnRoute" class="-mt-4">
      <button
        @click="router.push(returnRoute)"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 shadow-sm transition-colors"
      >
        <i class="fas fa-arrow-left mr-2"></i>
        Retour à la création de produit
      </button>
    </div>

    <!-- Section Création de Gamme -->
    <GammeCreationCard
      :all-declinaisons="allDeclinaisons"
      @create="handleCreateGamme"
    />

    <!-- Section Gestion des Gammes -->
    <div class="space-y-6">
      <h2 class="text-xl font-semibold text-black">Gammes existantes</h2>

      <div v-if="isLoading" class="text-center py-12">
        <div class="text-black">
          <i class="fas fa-spinner fa-spin text-4xl mb-4"></i>
          <p>Chargement des gammes...</p>
        </div>
      </div>

      <div v-else-if="gammes.length === 0" class="text-center py-12">
        <div class="text-black">
          <i class="fas fa-ruler text-4xl mb-4"></i>
          <p>Aucune gamme de tailles créée</p>
          <p class="text-sm">Créez votre première gamme ci-dessus</p>
        </div>
      </div>

      <div v-else class="space-y-4">
        <!-- Pagination haute -->
        <UnifiedPagination
          :total-items="gammes.length"
          :current-page="currentPage"
          :items-per-page="itemsPerPage"
          variant="product"
          item-label="gammes de tailles"
          @update:current-page="currentPage = $event"
          @update:items-per-page="itemsPerPage = $event"
        />

        <GammeCard
          v-for="gamme in paginatedGammes"
          :key="gamme.id_declinaison"
          :gamme="gamme"
          @edit="handleEditGamme"
          @delete="handleDeleteGamme"
        />
      </div>
    </div>

    <!-- Pagination -->
    <UnifiedPagination
      :total-items="gammes.length"
      :current-page="currentPage"
      :items-per-page="itemsPerPage"
      variant="product"
      item-label="gammes de tailles"
      @update:current-page="currentPage = $event"
      @update:items-per-page="itemsPerPage = $event"
    />
  </div>

  <!-- Modal de modification de gamme -->
  <ModalComp
    :is-visible="showEditModal"
    @update:is-visible="showEditModal = $event"
    title="Modifier la gamme de tailles"
    size="lg"
    :hide-footer="true"
  >
    <template #body>
      <GammeEditForm
        v-if="editingGamme"
        :gamme="editingGamme"
        @submit="handleEditSubmit"
        @cancel="showEditModal = false"
      />
    </template>
  </ModalComp>

  <ModalComp
    :is-visible="showDeleteModal"
    title="Supprimer la gamme de tailles"
    size="sm"
    :hide-footer="true"
    @update:is-visible="closeDeleteModal"
  >
    <template #body>
      <p v-if="isCheckingUsage" class="text-sm text-black">
        Vérification des produits associés...
      </p>

      <p v-else-if="hasBoundProducts" class="mb-4 text-sm text-black">
        Tous les produits utilisant
        <strong>« {{ deletingGamme?.titre || "Sans titre" }} »</strong>
        seront associés à la gamme sélectionnée.
      </p>

      <p v-else-if="hasBoundProducts === false" class="mb-4 text-sm text-black">
        Cette gamme n'est associée à aucun produit. Elle peut être supprimée
        sans remplacement.
      </p>

      <div v-if="hasBoundProducts && replacementOptions.length > 0">
        <label class="block mb-2 text-sm font-medium text-black">
          Gamme de remplacement
        </label>
        <select
          v-model.number="replacementGammeId"
          class="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">Sélectionner une gamme</option>
          <option
            v-for="option in replacementOptions"
            :key="option.gamme.id_declinaison"
            :value="option.gamme.id_declinaison"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <p v-else-if="hasBoundProducts" class="text-sm text-red-600">
        Créez d'abord une autre gamme avec le même nombre de tailles.
      </p>

      <div class="flex justify-end gap-3 pt-5">
        <button
          type="button"
          class="px-4 py-2 text-sm border border-gray-300 rounded-md"
          @click="closeDeleteModal(false)"
        >
          Annuler
        </button>
        <button
          type="button"
          :disabled="
            isDeleting ||
            isCheckingUsage ||
            hasBoundProducts === null ||
            (hasBoundProducts && !replacementGammeId)
          "
          class="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          @click="confirmDeleteGamme"
        >
          {{ isDeleting ? "Suppression..." : "Supprimer" }}
        </button>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import {
  PageHeader,
  UnifiedPagination,
  ModalComp,
  GammeCreationCard,
  GammeCard,
  GammeEditForm,
} from "@/components";
import {
  getGammesCompletesByVendeur,
  createGammeComplete,
  updateGammeComplete,
  declinaisonHasProducts,
  deleteGammeComplete,
} from "@/api/gammesTailles";
import { useAuthStore } from "@/stores/auth";
import { useNotification } from "@/composables/useNotification";
import type { GammeComplete, CreateGammeData } from "@/types";
import { useRouter } from "vue-router";
import { formatDeclinaisonLabel } from "@/utils/declinaisonLabel";

// Store d'authentification
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();

// Déterminer la page de retour selon le paramètre 'from'
const fromPage = computed(() => route.query.from as string | undefined);
const returnRoute = computed(() => {
  if (fromPage.value === 'mel') return '/catalog/mel';
  return null;
});
// Notifications
const { success: showSuccess, error: showError } = useNotification();

// État global de la page
const gammes = ref<GammeComplete[]>([]);
const allDeclinaisons = ref<GammeComplete[]>([]);
const currentPage = ref(1);
const itemsPerPage = ref(5);
const isLoading = ref(false);

// État de la modal de modification
const showEditModal = ref(false);
const editingGamme = ref<GammeComplete | null>(null);
const showDeleteModal = ref(false);
const deletingGamme = ref<GammeComplete | null>(null);
const replacementGammeId = ref<number | "">("");
const isDeleting = ref(false);
const isCheckingUsage = ref(false);
const hasBoundProducts = ref<boolean | null>(null);

const getGammeSizes = (gamme: GammeComplete): string[] =>
  [
    gamme.d1_FR,
    gamme.d2_FR,
    gamme.d3_FR,
    gamme.d4_FR,
    gamme.d5_FR,
    gamme.d6_FR,
    gamme.d7_FR,
    gamme.d8_FR,
    gamme.d9_FR,
    gamme.d10_FR,
    gamme.d11_FR,
    gamme.d12_FR,
  ]
    .map((size) => size?.trim())
    .filter((size): size is string => Boolean(size));

const replacementGammes = computed(() => {
  if (!deletingGamme.value) return [];
  const sizeCount = getGammeSizes(deletingGamme.value).length;
  return gammes.value.filter(
    (gamme) =>
      Number(gamme.id_declinaison) !==
        Number(deletingGamme.value?.id_declinaison) &&
      getGammeSizes(gamme).length === sizeCount,
  );
});

const getGammeLabel = (gamme: GammeComplete): string =>
  formatDeclinaisonLabel(gamme.titre?.trim() || "Sans titre", getGammeSizes(gamme));

const replacementOptions = computed(() => {
  return replacementGammes.value.map((gamme) => ({
    gamme,
    label: getGammeLabel(gamme),
  }));
});

// Pagination
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() =>
  Math.min(startIndex.value + itemsPerPage.value, gammes.value.length),
);
const paginatedGammes = computed(() => {
  return gammes.value.slice(startIndex.value, endIndex.value);
});

// Méthodes de gestion des données
const loadGammes = async () => {
  if (!authStore.user?.id_vendeur) {
    return;
  }

  isLoading.value = true;

  try {
    // Charger uniquement les gammes du vendeur
    const gammesData = await getGammesCompletesByVendeur(authStore.user.id_vendeur);
    gammes.value = gammesData;
    // Les templates "déclinaison existante" sont limités au vendeur courant
    allDeclinaisons.value = gammesData;
  } catch (err) {
    showError("Erreur lors du chargement des données");
  } finally {
    isLoading.value = false;
  }
};

const handleCreateGamme = async (formData: CreateGammeData) => {
  if (!authStore.user?.id_vendeur) {
    return;
  }

  try {
    const newGamme = await createGammeComplete(
      formData,
      authStore.user.id_vendeur,
    );
    gammes.value = [newGamme, ...gammes.value];
    currentPage.value = 1;

    showSuccess(`Gamme "${formData.titre}" créée avec succès !`);
  } catch (err) {
    showError(
      err instanceof Error
        ? err.message
        : "Erreur lors de la création de la gamme",
    );
  }
};

const handleEditGamme = (gamme: GammeComplete) => {
  editingGamme.value = { ...gamme };
  showEditModal.value = true;
};

const handleDeleteGamme = async (gamme: GammeComplete) => {
  deletingGamme.value = gamme;
  replacementGammeId.value = "";
  hasBoundProducts.value = null;
  showDeleteModal.value = true;
  isCheckingUsage.value = true;
  try {
    hasBoundProducts.value = await declinaisonHasProducts(
      Number(gamme.id_declinaison),
    );
  } catch (err) {
    showDeleteModal.value = false;
    deletingGamme.value = null;
    showError(
      err instanceof Error
        ? err.message
        : "Erreur lors de la vérification de la gamme",
    );
  } finally {
    isCheckingUsage.value = false;
  }
};

const closeDeleteModal = (visible: boolean) => {
  if (isDeleting.value) return;
  showDeleteModal.value = visible;
  if (!visible) {
    deletingGamme.value = null;
    replacementGammeId.value = "";
    hasBoundProducts.value = null;
  }
};

const confirmDeleteGamme = async () => {
  if (
    !deletingGamme.value ||
    hasBoundProducts.value === null ||
    (hasBoundProducts.value && !replacementGammeId.value) ||
    isDeleting.value
  ) {
    return;
  }

  isDeleting.value = true;
  try {
    const deletedTitle = deletingGamme.value.titre || "Sans titre";
    await deleteGammeComplete(
      Number(deletingGamme.value.id_declinaison),
      hasBoundProducts.value ? Number(replacementGammeId.value) : null,
    );
    showDeleteModal.value = false;
    deletingGamme.value = null;
    replacementGammeId.value = "";
    hasBoundProducts.value = null;
    await loadGammes();
    currentPage.value = Math.min(
      currentPage.value,
      Math.max(1, Math.ceil(gammes.value.length / itemsPerPage.value)),
    );
    showSuccess(`Gamme "${deletedTitle}" supprimée avec succès !`);
  } catch (err) {
    showError(
      err instanceof Error
        ? err.message
        : "Erreur lors de la suppression de la gamme",
    );
  } finally {
    isDeleting.value = false;
  }
};

const handleEditSubmit = async (updatedGamme: GammeComplete) => {
  try {
    // S'assurer que id_declinaison est un nombre
    const gammeToUpdate = {
      ...updatedGamme,
      id_declinaison: parseInt(updatedGamme.id_declinaison.toString()),
    };

    const result = await updateGammeComplete(gammeToUpdate);

    // Convertir les deux en nombres pour la comparaison
    const idToFind = parseInt(updatedGamme.id_declinaison.toString());
    const index = gammes.value.findIndex(
      (g) => parseInt(g.id_declinaison.toString()) === idToFind,
    );

    if (index !== -1) {
      gammes.value[index] = result;
      showSuccess(`Gamme "${updatedGamme.titre}" modifiée avec succès !`);
    } else {
      // Recharger toutes les gammes pour être sûr
      await loadGammes();
      showSuccess(`Gamme "${updatedGamme.titre}" modifiée avec succès !`);
    }

    showEditModal.value = false;
  } catch (err) {
    const message =
      err instanceof Error
        ? err.message
        : "Erreur lors de la modification de la gamme";
    console.error("Modification gamme:", err);
    showError(message);
  }
};

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: "auth" });
    return;
  }
  await loadGammes();
});
</script>
